import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Disc } from 'lucide-react';
import api from '../lib/api';
import { formatDate } from '../lib/format';
import { trackPageView } from '../utils/analytics';

const FALLBACK_POSTS = [
  { _id: '1', slug: 'screen-print-ritual', title: 'The Screen Print Ritual', excerpt: 'Why we hand-pull every layer of ink.', category: 'Process', publishedAt: '2024-10-12' },
  { _id: '2', slug: 'decoding-tantra', title: "Decoding 'Tantra'", excerpt: "It's not just aesthetic. It's about embracing the shadow self.", category: 'Lore', publishedAt: '2024-10-10' },
  { _id: '3', slug: 'inkspeak-origins', title: 'InkSpeak: Origins', excerpt: 'From dusty libraries to streetwear fabric.', category: 'Lore', publishedAt: '2024-09-28' },
  { _id: '4', slug: 'designing-the-chaos', title: 'Designing the Chaos', excerpt: 'Our messy, brutalist design philosophy explained.', category: 'Design', publishedAt: '2024-09-15' },
];

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('blog');
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/blogs');
        if (cancelled) return;
        const list = res.data?.data || [];
        if (list.length > 0) {
          setPosts(list);
        } else {
          setPosts(FALLBACK_POSTS);
          setUsingFallback(true);
        }
      } catch {
        if (cancelled) return;
        setPosts(FALLBACK_POSTS);
        setUsingFallback(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#e0e0e0] font-mono relative">
      <div className="bg-white border-b-4 border-black p-4 sticky top-20 z-20 shadow-xl">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl md:text-4xl font-black tracking-tighter bg-black text-white px-3">ARCHIVES_V.1.0</h1>
          <div className="hidden md:block font-bold text-xs">SYSTEM_STATUS: ONLINE</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="col-span-1 md:col-span-12 bg-yellow-300 border-4 border-black p-8 md:p-16 shadow-[16px_16px_0px_0px_rgba(0,0,0,1)] mb-8 relative overflow-hidden group">
          <div className="absolute top-0 right-0 bg-black text-white px-4 py-2 font-bold border-l-4 border-b-4 border-white z-10">FEATURED</div>
          <div className="relative z-10">
            <h2 className="text-5xl md:text-8xl font-black mb-4 uppercase leading-[0.9] mix-blend-multiply">
              The Process<br />Is The Ritual
            </h2>
            <p className="text-xl md:text-2xl font-bold mb-8 max-w-2xl border-l-8 border-black pl-6 py-2">
              Screen printing isn't just manufacturing. It's transferring soul onto cotton. Every squeegee pull is a prayer.
            </p>
            <div className="h-64 bg-neutral-900 w-full flex items-center justify-center text-white text-sm border-4 border-white border-dashed group-hover:border-solid transition-all">
              <div className="text-center">
                <Disc className="animate-spin-slow mx-auto mb-4" size={48} />
                [PLAY_VIDEO_SOURCE_01]
              </div>
            </div>
          </div>
          <div className="absolute -bottom-10 -right-10 text-[200px] font-black text-black opacity-5 pointer-events-none select-none">
            INK
          </div>
        </div>

        {loading ? (
          Array(4).fill(0).map((_, i) => (
            <div key={i} className="col-span-1 md:col-span-6 bg-white border-4 border-black p-8 animate-pulse">
              <div className="h-6 bg-gray-200 w-1/4 mb-3" />
              <div className="h-10 bg-gray-200 w-3/4 mb-2" />
              <div className="h-4 bg-gray-200" />
            </div>
          ))
        ) : (
          posts.map((post, idx) => (
            <article
              key={post._id || post.slug}
              className={`col-span-1 md:col-span-6 bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all relative ${
                idx % 2 === 0 ? 'md:rotate-1' : 'md:-rotate-1'
              } hover:rotate-0 hover:z-20`}
            >
              <div className="flex justify-between items-start mb-4 border-b-2 border-black pb-2">
                <span className="bg-black text-white px-2 py-1 text-xs font-bold uppercase">{post.category}</span>
                <span className="font-bold text-xs">{formatDate(post.publishedAt || post.date)}</span>
              </div>
              <Link to={`/blog/${post.slug || post._id}`} className="block">
                <h3 className="text-3xl font-black mb-4 hover:bg-yellow-300 inline-block transition-colors">
                  {post.title}
                </h3>
              </Link>
              <p className="text-gray-600 font-bold leading-relaxed">{post.excerpt}</p>
              <Link
                to={`/blog/${post.slug || post._id}`}
                className="mt-6 flex items-center justify-end gap-2 font-black text-sm uppercase group hover:gap-3 transition-all"
              >
                Read Entry <ArrowRight size={16} />
              </Link>
            </article>
          ))
        )}

        {usingFallback && (
          <div className="col-span-1 md:col-span-12 text-center text-xs font-mono opacity-50 py-4">
            Blog API not connected — showing sample entries.
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
